/**
 * Service to handle iOS PWA viewport scrolling, safe-area shifts, and virtual keyboard artifacts.
 * In iOS Safari Standalone PWA mode, focusing inputs causes WebKit to shift window.scrollY.
 * Upon blur, WebKit often fails to restore scrollY back to 0, leading to safe area misalignment.
 */

export function initIosPwaViewport(): () => void {
  if (typeof window === 'undefined') return () => {};

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true;

  // Even if not strictly standalone, apply viewport fix for touch devices with WebKit shift bugs
  if (!isIos && !isStandalone) {
    return () => {};
  }

  let scrollTimeout: any = null;

  const handleFocusOut = (e: FocusEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const isInput = target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable;

    if (isInput) {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const activeElement = document.activeElement;
        const activeIsInput = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          (activeElement as HTMLElement).isContentEditable
        );

        // Only reset scroll if user is not currently focusing another input
        if (!activeIsInput && window.scrollY !== 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          document.body.scrollTop = 0;
        }
      }, 120);
    }
  };

  const handleVisualViewportResize = () => {
    if (window.visualViewport && window.scrollY > 0) {
      const activeElement = document.activeElement;
      const activeIsInput = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA'
      );
      if (!activeIsInput) {
        window.scrollTo(0, 0);
      }
    }
  };

  window.addEventListener('focusout', handleFocusOut);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleVisualViewportResize);
  }

  return () => {
    window.removeEventListener('focusout', handleFocusOut);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
    }
  };
}
